import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * SplashScreen — Edge Elite
 * Splash de transición para login y logout.
 * Estilo: mockup oficial (fondo claro #F5F5F7, naranja #FF4D0D, Inter Tight).
 *
 * Props:
 *  - type: 'login' | 'logout'  → controla duración y mensaje aria-live
 *  - firstName: string         → mantenido por compatibilidad (no se usa visualmente)
 *  - minDuration: number?      → override de duración en ms
 *  - onComplete: () => void    → callback al finalizar
 */
export default function SplashScreen({ type = 'login', firstName, minDuration, onComplete }) {
  const [step, setStep] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const onCompleteRef = useRef(onComplete);

  // Mantener ref actualizada sin re-disparar el effect principal
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const effectiveDuration = minDuration ?? (type === 'login' ? 4000 : 3000);

  useEffect(() => {
    const timers = [
      setTimeout(() => setStep(1), 150),
      setTimeout(() => setStep(2), 600),
      setTimeout(() => setStep(3), 1100),
      setTimeout(() => setStep(4), 1600),
      setTimeout(() => {
        setFadeOut(true);
        if (typeof onCompleteRef.current === 'function') {
          onCompleteRef.current();
        }
      }, effectiveDuration - 400),
    ];
    return () => timers.forEach(clearTimeout);
  }, [effectiveDuration]);

  const ariaMessage =
    type === 'login' ? 'Iniciando sesión en Edge Elite' : 'Cerrando sesión de Edge Elite';

  const node = (
    <div
      className={`ee-splash${fadeOut ? ' ee-splash--out' : ''}`}
      role="status"
      aria-live="polite"
      aria-label={ariaMessage}
    >
      <div className={`ee-splash-content ee-splash-step-${step}`}>
        <div className="ee-lockup">
          <div className="ee-wordmark">Edge Elite</div>
          <svg className="ee-ball" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" aria-hidden="true">
            <circle cx="32" cy="32" r="30" fill="#FF4D0D" />
            <path
              d="M2 32h60 M32 2v60 M10 10c10 8 10 36 0 44 M54 10c-10 8-10 36 0 44"
              stroke="#0A0A0C"
              strokeOpacity="0.9"
              strokeWidth="1.8"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </div>

        <div className="ee-tagline">HECHO PARA GANAR CON VENTAJA REAL</div>

        <div className="ee-loader" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>

      <style>{`
        .ee-splash {
          position: fixed;
          inset: 0;
          background: #F5F5F7;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          overflow: hidden;
          opacity: 1;
          transition: opacity 380ms ease-out;
          animation: ee-splash-in 0.4s ease-out;
        }
        .ee-splash--out {
          opacity: 0;
          pointer-events: none;
        }
        .ee-splash::before {
          content: "";
          position: absolute;
          width: 700px;
          height: 700px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255,77,13,0.16) 0%, rgba(255,77,13,0.06) 35%, transparent 65%);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation: ee-splash-glow 3.5s ease-in-out infinite;
          pointer-events: none;
        }

        .ee-splash-content {
          position: relative;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 32px;
          padding: 0 24px;
          opacity: 0;
          transform: translateY(12px);
          transition: opacity 500ms ease-out, transform 500ms ease-out;
        }
        .ee-splash-step-1,
        .ee-splash-step-2,
        .ee-splash-step-3,
        .ee-splash-step-4 {
          opacity: 1;
          transform: translateY(0);
        }

        .ee-splash .ee-lockup {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .ee-splash .ee-wordmark {
          font-family: "Inter Tight", -apple-system, "SF Pro Display", system-ui, sans-serif;
          font-size: 44px;
          font-weight: 700;
          color: #0A0A0C;
          letter-spacing: -0.035em;
          line-height: 1;
        }
        .ee-splash .ee-ball {
          width: 56px;
          height: 56px;
          animation: ee-splash-ball 4s ease-in-out infinite;
        }

        .ee-splash .ee-tagline {
          font-family: "Inter Tight", -apple-system, "SF Pro Display", system-ui, sans-serif;
          font-size: 11px;
          font-weight: 600;
          color: #FF4D0D;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          opacity: 0;
          transform: translateY(6px);
          transition: opacity 400ms ease-out 0ms, transform 400ms ease-out 0ms;
        }
        .ee-splash-step-2 .ee-tagline,
        .ee-splash-step-3 .ee-tagline,
        .ee-splash-step-4 .ee-tagline {
          opacity: 1;
          transform: translateY(0);
        }

        .ee-splash .ee-loader {
          display: flex;
          gap: 8px;
          margin-top: 8px;
          opacity: 0;
          transition: opacity 400ms ease-out;
        }
        .ee-splash-step-3 .ee-loader,
        .ee-splash-step-4 .ee-loader {
          opacity: 1;
        }
        .ee-splash .ee-loader span {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #FF4D0D;
          animation: ee-splash-bounce 1.4s ease-in-out infinite;
        }
        .ee-splash .ee-loader span:nth-child(2) { animation-delay: 0.16s; }
        .ee-splash .ee-loader span:nth-child(3) { animation-delay: 0.32s; }

        @keyframes ee-splash-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes ee-splash-glow {
          0%, 100% { transform: translate(-50%, -50%) scale(1);    opacity: 0.85; }
          50%      { transform: translate(-50%, -50%) scale(1.08); opacity: 1; }
        }
        @keyframes ee-splash-bounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40%           { transform: scale(1);   opacity: 1; }
        }
        @keyframes ee-splash-ball {
          0%, 100% { transform: rotate(0deg) scale(1); }
          50%      { transform: rotate(8deg) scale(1.04); }
        }

        @media (max-width: 480px) {
          .ee-splash::before {
            width: 460px;
            height: 460px;
          }
          .ee-splash .ee-wordmark { font-size: 36px; }
          .ee-splash .ee-ball { width: 46px; height: 46px; }
          .ee-splash .ee-tagline { font-size: 10px; letter-spacing: 0.15em; }
          .ee-splash-content { gap: 28px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .ee-splash,
          .ee-splash::before,
          .ee-splash .ee-ball,
          .ee-splash .ee-loader span,
          .ee-splash-content {
            animation: none !important;
            transition: opacity 200ms ease-out !important;
            transform: none !important;
          }
        }
      `}</style>
    </div>
  );

  return createPortal(node, document.body);
}
